// src/pages/negocio/PedidosNegocio.js
//
// Pedidos entrantes del negocio con cambios de estado y asignación
// de domiciliario. Polling 10s.

import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Stack, Card, CardContent, CardActionArea, Chip, Alert,
  CircularProgress, Divider, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, MenuItem, TextField, Tab, Tabs, IconButton,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ChatIcon      from '@mui/icons-material/Chat';
import RefreshIcon   from '@mui/icons-material/Refresh';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon    from '@mui/icons-material/Cancel';

import { pedidos as apiPedidos } from '../../api/pedidosApi';
import { perfilNegocio } from '../../api/perfilApi';
import { formatoCOP } from '../../utils/format';

const ESTADO_COLOR = {
  nuevo: 'default', preparacion: 'info', en_ruta: 'warning',
  entregado: 'success', cancelado: 'error',
};
const ESTADO_LABEL = {
  nuevo: 'Nuevo', preparacion: 'Preparando', en_ruta: 'En ruta',
  entregado: 'Entregado', cancelado: 'Cancelado',
};

const TABS = [
  { key: 'activos',    label: 'Activos',    estados: ['nuevo','preparacion','en_ruta'] },
  { key: 'entregados', label: 'Entregados', estados: ['entregado'] },
  { key: 'cancelados', label: 'Cancelados', estados: ['cancelado'] },
];

export default function PedidosNegocio() {
  const navigate = useNavigate();
  const [tab, setTab]           = useState(0);
  const [items, setItems]       = useState([]);
  const [domis, setDomis]       = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError]       = useState('');
  const [detalle, setDetalle]   = useState(null);
  const [asignando, setAsignando] = useState(null); // { pedidoId, domiciliarioId }

  const cargar = useCallback(async () => {
    setError('');
    try {
      const r = await apiPedidos.listar({ pageSize: 100 });
      const list = Array.isArray(r) ? r : (r.items || r.Items || []);
      setItems(list);
    } catch (e) {
      setError(e.mensajeUsuario || 'No se pudieron cargar los pedidos.');
    } finally { setCargando(false); }
  }, []);

  useEffect(() => {
    cargar();
    const id = setInterval(cargar, 10000);
    return () => clearInterval(id);
  }, [cargar]);

  useEffect(() => {
    perfilNegocio.domiciliariosListar()
      .then(r => setDomis(Array.isArray(r) ? r : (r.items || [])))
      .catch(() => {});
  }, []);

  const cambiarEstado = async (pedidoId, nuevoEstado) => {
    try {
      await apiPedidos.cambiarEstado(pedidoId, nuevoEstado);
      cargar();
      if (detalle) {
        const d = await apiPedidos.detalle(pedidoId);
        setDetalle(d);
      }
    } catch (e) { setError(e.mensajeUsuario || 'No se pudo cambiar el estado.'); }
  };

  const asignarDomi = async () => {
    if (!asignando?.domiciliarioId) return;
    try {
      await apiPedidos.asignarDomiciliario(asignando.pedidoId, parseInt(asignando.domiciliarioId, 10));
      setAsignando(null);
      cargar();
      if (detalle) setDetalle(await apiPedidos.detalle(asignando.pedidoId));
    } catch (e) { setError(e.mensajeUsuario || 'No se pudo asignar el domiciliario.'); }
  };

  const estadosTab = TABS[tab].estados;
  const filtrados  = items.filter(p => estadosTab.includes(p.estado || p.Estado));

  return (
    <Stack spacing={3}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="overline" color="text.secondary">Pedidos</Typography>
          <Typography variant="h4" sx={{ fontFamily: 'Fraunces' }}>Pedidos entrantes</Typography>
        </Box>
        <Button startIcon={<RefreshIcon />} onClick={cargar}>Actualizar</Button>
      </Box>

      {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}

      <Card>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          {TABS.map(t => <Tab key={t.key} label={t.label} />)}
        </Tabs>
      </Card>

      {cargando ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : filtrados.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 5 }}>
            <Typography color="text.secondary">Sin pedidos en esta pestaña.</Typography>
          </CardContent>
        </Card>
      ) : (
        <Stack spacing={2}>
          {filtrados.map((p) => {
            const pid = p.pedidoId || p.PedidoId;
            const est = p.estado || p.Estado;
            const atenVenc = p.atencionVencida || p.AtencionVencida;
            return (
              <Card key={pid}>
                <CardActionArea onClick={async () => {
                  try { setDetalle(await apiPedidos.detalle(pid)); }
                  catch (e) { setError(e.mensajeUsuario || 'No se pudo cargar.'); }
                }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'flex-start' }}>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="overline" color="text.secondary">
                          {p.numeroPedido || p.NumeroPedido}
                        </Typography>
                        <Typography variant="h6" sx={{ fontFamily: 'Fraunces' }}>
                          {p.veciNombre || p.VeciNombre}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {new Date(p.fecha || p.Fecha).toLocaleString('es-CO', { dateStyle: 'medium', timeStyle: 'short' })}
                        </Typography>
                      </Box>
                      <Stack direction="column" spacing={0.5} alignItems="flex-end">
                        <Chip label={ESTADO_LABEL[est] || est} color={ESTADO_COLOR[est] || 'default'} />
                        {atenVenc && <Chip label="¡Vencido!" size="small" color="error" />}
                      </Stack>
                    </Box>
                    <Divider sx={{ my: 1.5 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                      <Typography variant="body2">
                        {p.cantidadItems || p.CantidadItems} producto(s) · {(p.metodoPago || p.MetodoPago)}
                      </Typography>
                      <Typography variant="h6" sx={{ color: 'primary.main' }}>
                        {formatoCOP(p.total || p.Total)}
                      </Typography>
                    </Box>
                  </CardContent>
                </CardActionArea>
              </Card>
            );
          })}
        </Stack>
      )}

      {/* Dialog detalle */}
      <Dialog open={!!detalle} onClose={() => setDetalle(null)} fullWidth maxWidth="sm">
        <DialogTitle>
          <Typography variant="overline" color="text.secondary">{detalle?.numeroPedido || detalle?.NumeroPedido}</Typography>
          <Typography variant="h5" sx={{ fontFamily: 'Fraunces' }}>{detalle?.veciNombre || detalle?.VeciNombre}</Typography>
        </DialogTitle>
        <DialogContent dividers>
          {detalle && (
            <Stack spacing={2}>
              <Chip
                label={ESTADO_LABEL[detalle.estado || detalle.Estado]}
                color={ESTADO_COLOR[detalle.estado || detalle.Estado] || 'default'}
                sx={{ alignSelf: 'flex-start' }}
              />

              <Box>
                <Typography variant="overline" color="text.secondary">Entrega</Typography>
                <Typography variant="body2">{detalle.direccionEntrega || detalle.DireccionEntrega}</Typography>
                {(detalle.instrucciones || detalle.Instrucciones) && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {detalle.instrucciones || detalle.Instrucciones}
                  </Typography>
                )}
              </Box>

              <Box>
                <Typography variant="overline" color="text.secondary">Método de pago</Typography>
                <Typography variant="body2">{detalle.metodoPago || detalle.MetodoPago}</Typography>
              </Box>

              <Divider />

              <Box>
                <Typography variant="overline" color="text.secondary">Productos</Typography>
                {(detalle.items || detalle.Items || []).map((it, i) => (
                  <Box key={i} sx={{ py: 0.5, display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">
                      {it.cantidad || it.Cantidad}× {it.nombre || it.Nombre}
                    </Typography>
                    <Typography variant="body2">{formatoCOP(it.subtotal || it.Subtotal)}</Typography>
                  </Box>
                ))}
              </Box>

              <Divider />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="h6">Total</Typography>
                <Typography variant="h6" sx={{ color: 'primary.main' }}>
                  {formatoCOP(detalle.total || detalle.Total)}
                </Typography>
              </Box>

              {(detalle.domiciliarioNombre || detalle.DomiciliarioNombre) && (
                <Chip
                  icon={<LocalShippingIcon />}
                  label={`Domi: ${detalle.domiciliarioNombre || detalle.DomiciliarioNombre}`}
                  variant="outlined"
                />
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1, flexWrap: 'wrap' }}>
          {detalle?.estado === 'nuevo' && (
            <>
              <Button
                variant="contained" startIcon={<CheckCircleIcon />}
                onClick={() => cambiarEstado(detalle.pedidoId || detalle.PedidoId, 'preparacion')}
              >
                Empezar a preparar
              </Button>
              <Button
                color="error" startIcon={<CancelIcon />}
                onClick={() => cambiarEstado(detalle.pedidoId || detalle.PedidoId, 'cancelado')}
              >
                Rechazar
              </Button>
            </>
          )}
          {detalle?.estado === 'preparacion' && domis.length > 0 && (
            <Button
              variant="contained" startIcon={<LocalShippingIcon />}
              onClick={() => setAsignando({ pedidoId: detalle.pedidoId || detalle.PedidoId, domiciliarioId: '' })}
            >
              Asignar domi y enviar
            </Button>
          )}
          {detalle?.estado === 'preparacion' && domis.length === 0 && (
            <Button
              variant="contained"
              onClick={() => cambiarEstado(detalle.pedidoId || detalle.PedidoId, 'en_ruta')}
            >
              Marcar en ruta
            </Button>
          )}
          {detalle?.estado === 'en_ruta' && (
            <Button
              variant="contained" color="success" startIcon={<CheckCircleIcon />}
              onClick={() => cambiarEstado(detalle.pedidoId || detalle.PedidoId, 'entregado')}
            >
              Marcar entregado
            </Button>
          )}
          <Button
            startIcon={<ChatIcon />}
            onClick={() => {
              const negId = detalle?.negocioId || detalle?.NegocioId;
              const veciId = detalle?.veciId || detalle?.VeciId;
              setDetalle(null);
              navigate(`/negocio/mensajes/${veciId}`);
            }}
          >
            Chat
          </Button>
          <Button onClick={() => setDetalle(null)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog asignar domi */}
      <Dialog open={!!asignando} onClose={() => setAsignando(null)} fullWidth maxWidth="xs">
        <DialogTitle>Asignar domiciliario</DialogTitle>
        <DialogContent>
          <TextField
            select fullWidth label="Domiciliario"
            value={asignando?.domiciliarioId || ''}
            onChange={(e) => setAsignando(s => ({ ...s, domiciliarioId: e.target.value }))}
            sx={{ mt: 1 }}
          >
            {domis.map(d => (
              <MenuItem key={d.id || d.Id} value={d.id || d.Id}>{d.nombre || d.Nombre}</MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAsignando(null)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={asignarDomi}
            disabled={!asignando?.domiciliarioId}
          >
            Asignar y enviar
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
